// src/components/Dashboard/HealthTab.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { app } from '../../firebase/firebaseConfig';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot
} from 'firebase/firestore';
import { getHistoryDocsNewestFirst } from '../../lib/sessionHistoryQueries';
import { useSessionStorageState } from './useSessionStorageState';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Activity,
  Users,
  Home,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Clock,
  Snowflake,
  Zap,
  Package,
  Truck,
  History
} from 'lucide-react';
import FamilyTimeline from './FamilyTimeline';
import { getLocationDisplay, extractIglooId } from './locationDisplay';
import { useIglooNames } from './useIglooNames';

function toMs(ts) {
  if (!ts) return 0;
  if (ts.toMillis) return ts.toMillis();
  if (typeof ts === 'number') return ts;
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}

// WiFi signal strength bars icon (1-4 bars based on RSSI dBm)
function WifiSignalBars({ rssiDbm }) {
  // Determine number of filled bars from RSSI
  let bars;
  let color;
  if (rssiDbm > -50) {
    bars = 4; color = '#22c55e'; // green-500 — excellent
  } else if (rssiDbm > -60) {
    bars = 3; color = '#22c55e'; // green-500 — good
  } else if (rssiDbm > -70) {
    bars = 2; color = '#f97316'; // orange-500 — fair
  } else {
    bars = 1; color = '#ef4444'; // red-500 — poor
  }

  const barWidth = 2.5;
  const gap = 1.5;
  const heights = [4, 7, 10, 13];
  const totalWidth = 4 * barWidth + 3 * gap;

  return (
    <svg
      width={totalWidth}
      height={14}
      viewBox={`0 0 ${totalWidth} 14`}
      className="flex-shrink-0"
      title={`WiFi RSSI: ${Math.round(rssiDbm)} dBm`}
    >
      {heights.map((h, i) => (
        <rect
          key={i}
          x={i * (barWidth + gap)}
          y={14 - h}
          width={barWidth}
          height={h}
          rx={0.5}
          fill={i < bars ? color : '#d1d5db'}
        />
      ))}
    </svg>
  );
}

// Helper to format relative time
function formatRelativeTime(timestampMs) {
  if (!timestampMs) return 'Never';

  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Helper to format duration
function formatDuration(durationMs) {
  if (!durationMs || durationMs < 0) return '-';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${days}d ${hours % 24}h`;
}

// Helper to format seconds as hours (with 1 decimal place)
function formatSecondsAsHours(seconds) {
  if (!seconds || seconds < 0) return '0';
  const hours = seconds / 3600;
  if (hours < 0.1) return '<0.1';
  if (hours < 10) return hours.toFixed(1);
  return Math.round(hours).toString();
}

// Helper to format crash reason for display.
// Handles the new granular boot_reason taxonomy (see BootClassifier.kt) plus
// the legacy strings for events written before the cutover.
function formatCrashReason(reason) {
  switch (reason) {
    // New granular crash reasons
    case 'app_crash_java':
      return 'App Crash (Java)';
    case 'app_crash_native':
      return 'App Crash (Native)';
    case 'app_anr':
      return 'App Not Responding';
    case 'app_init_failure':
      return 'App Init Failure';
    case 'app_exit_self_error':
      return 'App Self-Exit (Error)';
    case 'app_oom':
      return 'App Out-of-Memory Kill';
    case 'app_killed_signal':
      return 'App Killed (Signal)';
    case 'app_killed_resource':
      return 'App Killed (Resource)';
    case 'app_dependency_died':
      return 'App Dependency Died';
    case 'app_exit_other':
      return 'App Exit (Other)';
    case 'app_exit_unknown':
      return 'App Exit (Unknown)';
    case 'app_restart_clean':
      return 'App Restart (Clean)';
    case 'app_restart_config':
      return 'App Restart (Config)';
    case 'app_restart_unmonitored':
      return 'App Restart (Unmonitored)';
    // New power-cycle reasons
    case 'power_cycle_fast':
      return 'Power Cycle (Fast — likely user)';
    case 'power_cycle':
      return 'Power Cycle';
    case 'power_cycle_unknown':
      return 'Power Cycle (Unknown gap)';
    // New update / first-boot reasons
    case 'restart_from_update':
      return 'Restart from APK Update';
    case 'os_updated':
      return 'OS Updated';
    case 'first_boot':
      return 'First Boot';
    // Pre-cutover boot_reason values (legacy)
    case 'app_crash':
      return 'App Crash';
    case 'power_cycled':
      return 'Power Cycled (Hardware Issue)';
    case 'power_switched_off':
      return 'Power Switched Off';
    case 'app_restart_from_update':
      return 'OTA Update';
    case 'power_loss_active':
      return 'Power Loss (Active)';
    case 'power_loss_in_igloo':
      return 'Power Loss (In Call)';
    case 'power_loss_energy_saver':
      return 'Power Loss (Energy Saver)';
    default:
      return reason || 'Unknown';
  }
}

// Helper to check if a boot event represents an app crash (software issue).
// Prefers the new `boot_category` field; falls back to legacy `boot_reason`
// values for events written before the cutover.
function isAppCrash(eventData) {
  if (!eventData) return false;
  if (eventData.boot_category) {
    return eventData.boot_category === 'crash';
  }
  return eventData.boot_reason === 'app_crash';
}

// Helper to check if a boot event represents a power cycle (hardware issue).
function isPowerCycle(eventData) {
  if (!eventData) return false;
  if (eventData.boot_category) {
    return eventData.boot_category === 'power_cycle';
  }
  return eventData.boot_reason === 'power_cycled' ||
         // Legacy values that indicate power issues
         eventData.boot_reason === 'power_loss_active' ||
         eventData.boot_reason === 'power_loss_in_igloo';
}

// Helper to format last_state for display
function formatLastState(state) {
  switch (state) {
    case 'energy_saver':
      return 'sleep';
    case 'active':
      return 'home';
    case 'in_igloo':
      return 'call';
    case 'training':
      return 'training';
    case 'sign_in':
      return 'sign-in';
    case 'updating':
      return 'updating';
    default:
      return state || '?';
  }
}

function HealthTab() {
  const [accounts, setAccounts] = useState([]);
  const [expandedAccounts, setExpandedAccounts] = useSessionStorageState('higlo.admin.expandedAccounts.health', {});
  const [familyData, setFamilyData] = useState({}); // familyId -> family health data
  const [familyListeners, setFamilyListeners] = useState({}); // Track active listeners
  // Feed the shared hook the igloo IDs on screen so it can resolve any that
  // appeared after the initial scan (a device entering a brand-new igloo).
  const neededIglooIds = useMemo(
    () => Array.from(new Set(
      Object.values(familyData).map((f) => extractIglooId(f?.currentScreen)).filter(Boolean)
    )),
    [familyData]
  );
  const iglooNames = useIglooNames(true, neededIglooIds); // iglooId -> name (shared hook)
  const [loading, setLoading] = useState(true);
  const [calculatedData, setCalculatedData] = useState({}); // familyId -> calculated analytics data
  const [calculatingFamilies, setCalculatingFamilies] = useState(new Set()); // Families currently being calculated
  const [accountLastRefreshed, setAccountLastRefreshed] = useState({}); // accountId -> timestamp
  const [globalLastRefreshed, setGlobalLastRefreshed] = useState(null); // timestamp for global refresh
  const [isGlobalRefreshing, setIsGlobalRefreshing] = useState(false); // track global refresh in progress
  const [, forceUpdate] = useState(0); // Used to trigger re-renders for online status updates
  const [showTimeline, setShowTimeline] = useState(false); // Show timeline view
  const [selectedTimelineFamily, setSelectedTimelineFamily] = useState(null); // { familyId, familyName }
  const [healthSortMode, setHealthSortMode] = useState('alphabetic');
  const [firstSigninTimes, setFirstSigninTimes] = useState({}); // familyId -> earliest signed_in timestamp (ms)
  const [lastIglooTimes, setLastIglooTimes] = useState({}); // familyId -> most recent igloo timestamp (ms)
  const [iglooTimesLoading, setIglooTimesLoading] = useState(false);

  // Helper to compute isOnline from lastOnlinePing timestamp
  const computeIsOnline = useCallback((lastOnlinePing) => {
    if (!lastOnlinePing) return false;
    const pingPeriodSeconds = 30;
    const pingBufferSeconds = 5;
    return Date.now() - lastOnlinePing < (pingPeriodSeconds + pingBufferSeconds) * 1000;
  }, []);

  // Periodic timer to re-evaluate online status (every 5 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      forceUpdate(n => n + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Fetch accounts on mount
  useEffect(() => {
    const db = getFirestore(app);

    const fetchAccounts = async () => {
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'accountsInfo'));
        const accountsList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Sort accounts by email
        accountsList.sort((a, b) => (a.email || '').localeCompare(b.email || ''));
        setAccounts(accountsList);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, []);

  // Fetch first sign-in times for all families (for sorting)
  useEffect(() => {
    if (accounts.length === 0) return;
    const db = getFirestore(app);

    const fetchSigninTimes = async () => {
      const allFamilyIds = new Set();
      accounts.forEach(account => {
        (account.families || []).forEach(fid => allFamilyIds.add(fid));
      });
      const times = {};
      await Promise.all(
        Array.from(allFamilyIds).map(async (familyId) => {
          try {
            const familySnap = await getDoc(doc(db, 'familyInfo', familyId));
            if (familySnap.exists() && familySnap.data().firstSignin) {
              times[familyId] = toMs(familySnap.data().firstSignin);
            }
          } catch (e) { /* ignore */ }
        })
      );
      setFirstSigninTimes(times);
    };

    fetchSigninTimes();
  }, [accounts]);

  // Setup listeners for a family's real-time data
  const setupFamilyListeners = useCallback((familyId) => {
    const db = getFirestore(app);
    const listeners = [];

    // Listen to familyInfo for setup status (exists = setup complete)
    const familyInfoRef = doc(db, 'familyInfo', familyId);
    const unsubFamilyInfo = onSnapshot(familyInfoRef, (docSnap) => {
      setFamilyData(prev => ({
        ...prev,
        [familyId]: {
          ...prev[familyId],
          familyInfo: docSnap.exists() ? docSnap.data() : null,
          familyInfoExists: docSnap.exists(),
          // Setup is complete if familyInfo document exists (created during account setup)
          isSetup: docSnap.exists(),
          // Training complete is a separate field
          trainingComplete: docSnap.exists() && docSnap.data().trainingComplete === true
        }
      }));
    }, (error) => {
      console.error(`Error listening to familyInfo for ${familyId}:`, error);
    });
    listeners.push(unsubFamilyInfo);

    // Listen to deviceManagement for general device data
    const deviceMgmtRef = doc(db, 'deviceManagement', familyId);
    const unsubDeviceMgmt = onSnapshot(deviceMgmtRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        const fallbackPingDate = data.onlinePing?.toDate();

        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            deviceManagement: data,
            hasEverSignedIn: true,
            fallbackOnlinePing: fallbackPingDate?.getTime() || null,
            lastOnlinePing: prev[familyId]?.statusOnlinePing ?? fallbackPingDate?.getTime() ?? null
          }
        }));
      } else {
        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            deviceManagement: null,
            hasEverSignedIn: false,
            fallbackOnlinePing: null,
            lastOnlinePing: prev[familyId]?.statusOnlinePing ?? null
          }
        }));
      }
    }, (error) => {
      console.error(`Error listening to deviceManagement for ${familyId}:`, error);
    });
    listeners.push(unsubDeviceMgmt);

    const statusPublishedRef = doc(db, 'deviceManagement', familyId, 'status', 'everyFamilyAndAccountVisible');
    const unsubStatusPublished = onSnapshot(statusPublishedRef, (docSnap) => {
      if (docSnap.exists()) {
        const pingDate = docSnap.data().onlinePing?.toDate();
        const pingMs = pingDate?.getTime() || null;
        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            statusOnlinePing: pingMs,
            lastOnlinePing: pingMs ?? prev[familyId]?.fallbackOnlinePing ?? null
          }
        }));
      } else {
        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            statusOnlinePing: null,
            lastOnlinePing: prev[familyId]?.fallbackOnlinePing ?? null
          }
        }));
      }
    }, (error) => {
      console.error(`Error listening to status/everyFamilyAndAccountVisible for ${familyId}:`, error);
    });
    listeners.push(unsubStatusPublished);

    // Live screen/location and WiFi RSSI come from status/accountVisible — same
    // doc the user webapp reads (see useFamiliesData.js post-89e3b96d). The
    // older log/system path carries only the sampled time-series log.
    const accountVisibleRef = doc(db, 'deviceManagement', familyId, 'status', 'accountVisible');
    const unsubAccountVisible = onSnapshot(accountVisibleRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            currentScreen: data.location || null,
            wifiRssiDbm: typeof data.rssi === 'number' ? data.rssi : null
          }
        }));
      } else {
        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            currentScreen: null,
            wifiRssiDbm: null
          }
        }));
      }
    }, (error) => {
      console.error(`Error listening to status/accountVisible for ${familyId}:`, error);
    });
    listeners.push(unsubAccountVisible);

    return listeners;
  }, []);

  // Clean up listeners for a family
  const cleanupFamilyListeners = useCallback((familyId) => {
    const listeners = familyListeners[familyId];
    if (listeners) {
      listeners.forEach(unsub => unsub());
      setFamilyListeners(prev => {
        const newListeners = { ...prev };
        delete newListeners[familyId];
        return newListeners;
      });
    }
  }, [familyListeners]);

  // Toggle account expansion
  const toggleAccount = useCallback((accountId, families) => {
    const isExpanding = !expandedAccounts[accountId];

    setExpandedAccounts(prev => ({
      ...prev,
      [accountId]: isExpanding
    }));

    if (isExpanding && families) {
      // Setup listeners for all families in this account
      families.forEach(familyId => {
        if (!familyListeners[familyId]) {
          const listeners = setupFamilyListeners(familyId);
          setFamilyListeners(prev => ({
            ...prev,
            [familyId]: listeners
          }));
        }
      });
    }
  }, [expandedAccounts, setExpandedAccounts, familyListeners, setupFamilyListeners]);

  // Expand all accounts
  const expandAllAccounts = useCallback(() => {
    const newExpanded = {};
    accounts.forEach(account => {
      newExpanded[account.id] = true;
      // Setup listeners for all families in each account
      const families = account.families || [];
      families.forEach(familyId => {
        if (!familyListeners[familyId]) {
          const listeners = setupFamilyListeners(familyId);
          setFamilyListeners(prev => ({
            ...prev,
            [familyId]: listeners
          }));
        }
      });
    });
    setExpandedAccounts(newExpanded);
  }, [accounts, setExpandedAccounts, familyListeners, setupFamilyListeners]);

  // Collapse all accounts
  const collapseAllAccounts = useCallback(() => {
    setExpandedAccounts({});
  }, [setExpandedAccounts]);

  // Restore family listeners for accounts that came back expanded from
  // sessionStorage — the expansion map persists, but the listeners died with
  // the previous unmount. Same guarded wiring as expandAllAccounts.
  useEffect(() => {
    accounts.forEach(account => {
      if (!expandedAccounts[account.id]) return;
      (account.families || []).forEach(familyId => {
        if (!familyListeners[familyId]) {
          const listeners = setupFamilyListeners(familyId);
          setFamilyListeners(prev => ({
            ...prev,
            [familyId]: listeners
          }));
        }
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore-on-load; the !familyListeners guard makes re-runs no-ops
  }, [accounts]);

  // Fetch last igloo times for all families (called lazily when "Recent Igloo" sort is selected)
  const fetchIglooTimes = useCallback(async () => {
    if (iglooTimesLoading) return;
    setIglooTimesLoading(true);
    const db = getFirestore(app);
    const allFamilyIds = new Set();
    accounts.forEach(account => {
      (account.families || []).forEach(fid => allFamilyIds.add(fid));
    });
    const times = {};
    try {
      // Fetch live sessions once
      const liveSnap = await getDocs(collection(db, 'iglooSessionsLive'));
      for (const sessionDoc of liveSnap.docs) {
        const participants = sessionDoc.data().participants || [];
        for (const participant of participants) {
          if (!allFamilyIds.has(participant.familyId)) continue;
          const intervals = participant.intervals || [];
          const lastInterval = intervals[intervals.length - 1];
          if (lastInterval?.joinedAt && !lastInterval?.leftAt) {
            times[participant.familyId] = Date.now();
          } else if (lastInterval?.leftAt) {
            const leftTime = lastInterval.leftAt.toMillis ? lastInterval.leftAt.toMillis() : lastInterval.leftAt;
            if (!times[participant.familyId] || leftTime > times[participant.familyId]) {
              times[participant.familyId] = leftTime;
            }
          }
        }
      }
    } catch (e) { /* ignore */ }
    // Fetch history per family
    await Promise.all(
      Array.from(allFamilyIds).map(async (familyId) => {
        try {
          // Newest sessions first (composite index); falls back to the previous
          // unordered limit(50) query until the index is deployed.
          const { docs: historyDocs } = await getHistoryDocsNewestFirst(db, { familyId }, { limit: 5, legacyLimit: 50 });
          for (const sessionDoc of historyDocs) {
            const endedAt = sessionDoc.data().endedAt;
            const endTime = endedAt?.toMillis ? endedAt.toMillis() : endedAt;
            if (endTime && (!times[familyId] || endTime > times[familyId])) {
              times[familyId] = endTime;
            }
          }
        } catch (e) { /* ignore */ }
      })
    );
    setLastIglooTimes(times);
    setIglooTimesLoading(false);
  }, [accounts, iglooTimesLoading]);

  // Sort helpers for health tab
  const getAccountSetupTime = useCallback((account) => {
    const families = account.families || [];
    let earliest = null;
    families.forEach(fid => {
      const t = firstSigninTimes[fid];
      if (t && (earliest === null || t < earliest)) earliest = t;
    });
    return earliest;
  }, [firstSigninTimes]);

  const getAccountLatestSignin = useCallback((account) => {
    const families = account.families || [];
    let latest = null;
    families.forEach(fid => {
      const t = firstSigninTimes[fid];
      if (t && (latest === null || t > latest)) latest = t;
    });
    return latest;
  }, [firstSigninTimes]);

  const getAccountLatestIgloo = useCallback((account) => {
    const families = account.families || [];
    let latest = null;
    families.forEach(fid => {
      const t = lastIglooTimes[fid];
      if (t && (latest === null || t > latest)) latest = t;
    });
    return latest;
  }, [lastIglooTimes]);

  const sortedAccounts = [...accounts].sort((a, b) => {
    switch (healthSortMode) {
      case 'recentSetup': {
        const aTime = getAccountSetupTime(a) || 0;
        const bTime = getAccountSetupTime(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      case 'recentSignin': {
        const aTime = getAccountLatestSignin(a) || 0;
        const bTime = getAccountLatestSignin(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      case 'recentIgloo': {
        const aTime = getAccountLatestIgloo(a) || 0;
        const bTime = getAccountLatestIgloo(b) || 0;
        if (aTime !== bTime) return bTime - aTime;
        return (a.email || '').localeCompare(b.email || '');
      }
      default:
        return (a.email || '').localeCompare(b.email || '');
    }
  });

  // Calculate analytics data for a family (crash info, igloo time, online duration, etc.)
  const calculateFamilyAnalytics = useCallback(async (familyId) => {
    const db = getFirestore(app);

    setCalculatingFamilies(prev => new Set([...prev, familyId]));

    try {
      const now = Date.now();
      const oneDayAgo = now - (24 * 60 * 60 * 1000);
      const sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000);

      // Also refresh the current screen by re-reading it
      const accountVisibleRef = doc(db, 'deviceManagement', familyId, 'status', 'accountVisible');
      const accountVisibleSnap = await getDoc(accountVisibleRef);
      if (accountVisibleSnap.exists()) {
        const data = accountVisibleSnap.data();
        setFamilyData(prev => ({
          ...prev,
          [familyId]: {
            ...prev[familyId],
            currentScreen: data.location || null
          }
        }));
      }

      // Fetch events from deviceEvents collection
      const eventsRef = collection(db, 'deviceEvents', familyId, 'events');
      const eventsSnapshot = await getDocs(eventsRef);

      // App crashes (software issues)
      let lastCrashEvent = null;
      let crashesLast24h = 0;
      let crashesLast7d = 0;

      // Power cycles (hardware issues)
      let lastPowerCycleEvent = null;
      let powerCyclesLast24h = 0;
      let powerCyclesLast7d = 0;

      let lastBootTime = null; // When did they last come online (boot event)
      let lastIglooExitedTime = null;
      let lastIglooExitedId = null;

      eventsSnapshot.docs.forEach(eventDoc => {
        const data = eventDoc.data();
        const eventTime = data.timestamp; // This is stored as milliseconds, not a Firestore timestamp

        // Check for boot events
        if (data.event_type === 'boot') {
          const bootReason = data.boot_reason || '';

          // Track last boot time (for "online since" calculation)
          if (!lastBootTime || eventTime > lastBootTime) {
            lastBootTime = eventTime;
          }

          // Check if it's an app crash
          if (isAppCrash(data)) {
            if (eventTime > oneDayAgo) crashesLast24h++;
            if (eventTime > sevenDaysAgo) crashesLast7d++;

            if (!lastCrashEvent || eventTime > lastCrashEvent.timestamp) {
              lastCrashEvent = {
                timestamp: eventTime,
                reason: bootReason,
                lastState: data.last_state
              };
            }
          }

          // Check if it's a power cycle
          if (isPowerCycle(data)) {
            if (eventTime > oneDayAgo) powerCyclesLast24h++;
            if (eventTime > sevenDaysAgo) powerCyclesLast7d++;

            if (!lastPowerCycleEvent || eventTime > lastPowerCycleEvent.timestamp) {
              lastPowerCycleEvent = {
                timestamp: eventTime,
                reason: bootReason,
                lastState: data.last_state
              };
            }
          }
        }

        // Track igloo exit times from device events
        if (data.event_type === 'igloo_exited') {
          if (!lastIglooExitedTime || eventTime > lastIglooExitedTime) {
            lastIglooExitedTime = eventTime;
            lastIglooExitedId = data.igloo_id;
          }
        }
      });

      const currentScreen = familyData[familyId]?.currentScreen || null;
      const lastOnlinePingValue = familyData[familyId]?.lastOnlinePing;
      const isOnline = computeIsOnline(lastOnlinePingValue);
      const isInIgloo = isOnline && currentScreen &&
                       currentScreen !== 'home' &&
                       currentScreen !== 'training' &&
                       currentScreen !== 'sleep' &&
                       currentScreen !== 'loading';

      // For "since igloo" we need to find when this family last left ANY igloo
      // This could be:
      // 1. They're currently in an igloo (show "In Igloo For")
      // 2. They left an igloo that's still live (check iglooSessionsLive for their leftAt)
      // 3. They left an igloo that's now in history (check iglooSessionsHistory)
      // 4. Fallback to igloo_exited events from deviceEvents

      let iglooTimeInfo = null;
      let currentIglooStartTime = null;
      let lastLeftIglooTime = null;

      // First, check ALL live igloo sessions to see if this family is in one or recently left one
      try {
        const liveSessionsSnapshot = await getDocs(collection(db, 'iglooSessionsLive'));

        for (const sessionDoc of liveSessionsSnapshot.docs) {
          const sessionData = sessionDoc.data();
          const participant = sessionData.participants?.find(p => p.familyId === familyId);

          if (participant && participant.intervals?.length > 0) {
            const lastInterval = participant.intervals[participant.intervals.length - 1];

            if (lastInterval.leftAt) {
              // They left this igloo but session is still live (others still in it)
              const leftTime = lastInterval.leftAt.toDate?.()?.getTime() ||
                              (typeof lastInterval.leftAt === 'number' ? lastInterval.leftAt : null);
              if (leftTime && (!lastLeftIglooTime || leftTime > lastLeftIglooTime)) {
                lastLeftIglooTime = leftTime;
              }
            } else if (lastInterval.joinedAt && !lastInterval.leftAt) {
              // They're currently in this igloo
              const joinedTime = lastInterval.joinedAt.toDate?.()?.getTime() ||
                                (typeof lastInterval.joinedAt === 'number' ? lastInterval.joinedAt : null);
              if (joinedTime && (!currentIglooStartTime || joinedTime > currentIglooStartTime)) {
                currentIglooStartTime = joinedTime;
              }
            }
          }
        }
      } catch (e) {
        console.warn(`Could not fetch live sessions for ${familyId}:`, e);
      }

      // Track igloo time and session counts for 24h/7d
      let iglooSeconds24h = 0;
      let iglooSeconds7d = 0;
      let iglooSessions24h = 0;
      let iglooSessions7d = 0;

      // Also check recent history sessions for this family's most recent exit AND calculate stats
      try {
        // This family's 200 most recent sessions (composite index); falls back to
        // the previous unordered limit(200) query until the index is deployed.
        const { docs: historyDocs } = await getHistoryDocsNewestFirst(db, { familyId }, { limit: 200, legacyLimit: 200 });

        for (const historyDoc of historyDocs) {
          const historyData = historyDoc.data();
          const endedAtMs = historyData.endedAt?.toMillis ? historyData.endedAt.toMillis() : historyData.endedAt;

          // Find this family's participant record
          const participant = historyData.participants?.find(p => p.familyId === familyId);

          if (participant) {
            // Track last left time for "since igloo" calculation
            if (participant.intervals?.length > 0) {
              const lastInterval = participant.intervals[participant.intervals.length - 1];
              const leftTime = lastInterval.leftAt?.toMillis ? lastInterval.leftAt.toMillis() :
                              (lastInterval.leftAt?.toDate?.()?.getTime()) ||
                              (typeof lastInterval.leftAt === 'number' ? lastInterval.leftAt : null);

              if (leftTime && (!lastLeftIglooTime || leftTime > lastLeftIglooTime)) {
                lastLeftIglooTime = leftTime;
              }
            }

            // Count sessions and time based on when the session ended
            if (endedAtMs) {
              const participantSeconds = participant.totalSeconds || 0;

              if (endedAtMs > oneDayAgo) {
                iglooSessions24h++;
                iglooSeconds24h += participantSeconds;
              }
              if (endedAtMs > sevenDaysAgo) {
                iglooSessions7d++;
                iglooSeconds7d += participantSeconds;
              }
            }
          }
        }
      } catch (e) {
        console.warn(`Could not fetch history sessions for ${familyId}:`, e);
      }

      // Also add time from live sessions (if they're currently in one)
      // We already iterated through live sessions above, but we need to add time calculation
      try {
        const liveSessionsSnapshot = await getDocs(collection(db, 'iglooSessionsLive'));

        for (const sessionDoc of liveSessionsSnapshot.docs) {
          const sessionData = sessionDoc.data();
          const participant = sessionData.participants?.find(p => p.familyId === familyId);

          if (participant && participant.intervals?.length > 0) {
            // Calculate total time this family has spent in this live session
            let liveSessionSeconds = 0;
            for (const interval of participant.intervals) {
              const joinedMs = interval.joinedAt?.toMillis ? interval.joinedAt.toMillis() :
                              (interval.joinedAt?.toDate?.()?.getTime()) ||
                              (typeof interval.joinedAt === 'number' ? interval.joinedAt : null);
              const leftMs = interval.leftAt ?
                            (interval.leftAt?.toMillis ? interval.leftAt.toMillis() :
                             (interval.leftAt?.toDate?.()?.getTime()) ||
                             (typeof interval.leftAt === 'number' ? interval.leftAt : null)) : now;

              if (joinedMs) {
                liveSessionSeconds += Math.floor((leftMs - joinedMs) / 1000);
              }
            }

            // Check if this counts toward 24h/7d (based on session start time)
            const startedAtMs = sessionData.startedAt?.toMillis ? sessionData.startedAt.toMillis() : sessionData.startedAt;

            // A live session counts as a session for stats purposes
            if (startedAtMs > oneDayAgo || now > oneDayAgo) {
              iglooSessions24h++;
              iglooSeconds24h += liveSessionSeconds;
            }
            if (startedAtMs > sevenDaysAgo || now > sevenDaysAgo) {
              iglooSessions7d++;
              iglooSeconds7d += liveSessionSeconds;
            }
          }
        }
      } catch (e) {
        console.warn(`Could not fetch live sessions for igloo stats for ${familyId}:`, e);
      }

      // Compare with igloo_exited events and use the most recent
      if (lastIglooExitedTime && (!lastLeftIglooTime || lastIglooExitedTime > lastLeftIglooTime)) {
        lastLeftIglooTime = lastIglooExitedTime;
      }

      // Determine igloo time info
      if (isInIgloo && currentIglooStartTime) {
        // Currently in igloo - show how long they've been in
        iglooTimeInfo = {
          type: 'in_igloo',
          duration: now - currentIglooStartTime
        };
      } else if (lastLeftIglooTime) {
        // Not in igloo - show how long since they were in one
        iglooTimeInfo = {
          type: 'since_igloo',
          duration: now - lastLeftIglooTime
        };
      }

      // Calculate "online for" duration
      // If online now and we have a boot event, calculate time since that boot
      let onlineDuration = null;
      if (isOnline && lastBootTime) {
        onlineDuration = now - lastBootTime;
      }

      setCalculatedData(prev => ({
        ...prev,
        [familyId]: {
          lastCrash: lastCrashEvent,
          crashesLast24h,
          crashesLast7d,
          lastPowerCycle: lastPowerCycleEvent,
          powerCyclesLast24h,
          powerCyclesLast7d,
          iglooTimeInfo,
          iglooSeconds24h,
          iglooSeconds7d,
          iglooSessions24h,
          iglooSessions7d,
          onlineDuration,
          lastBootTime,
          calculatedAt: now
        }
      }));

    } catch (error) {
      console.error(`Error calculating analytics for ${familyId}:`, error);
    } finally {
      setCalculatingFamilies(prev => {
        const newSet = new Set(prev);
        newSet.delete(familyId);
        return newSet;
      });
    }
  }, [familyData, computeIsOnline]);

  // Refresh all analytics for an account
  const refreshAccountAnalytics = useCallback(async (accountId, families) => {
    if (!families) return;

    for (const familyId of families) {
      await calculateFamilyAnalytics(familyId);
    }

    // Update the last refreshed timestamp for this account
    setAccountLastRefreshed(prev => ({
      ...prev,
      [accountId]: Date.now()
    }));
  }, [calculateFamilyAnalytics]);

  // Refresh all analytics for ALL expanded accounts
  const refreshAllAnalytics = useCallback(async () => {
    setIsGlobalRefreshing(true);

    try {
      // Get all expanded accounts and their families
      for (const account of accounts) {
        if (expandedAccounts[account.id]) {
          const families = account.families || [];
          for (const familyId of families) {
            await calculateFamilyAnalytics(familyId);
          }
          // Update the last refreshed timestamp for this account
          setAccountLastRefreshed(prev => ({
            ...prev,
            [account.id]: Date.now()
          }));
        }
      }

      setGlobalLastRefreshed(Date.now());
    } finally {
      setIsGlobalRefreshing(false);
    }
  }, [accounts, expandedAccounts, calculateFamilyAnalytics]);

  // Cleanup listeners on unmount. The map lives in a ref so this can be a
  // true unmount-only cleanup — keying the effect on the map would run the
  // previous cleanup on every change, unsubscribing listeners that are still
  // in the new map (expand account A then B → A's rows freeze).
  const familyListenersRef = useRef(familyListeners);
  familyListenersRef.current = familyListeners;
  useEffect(() => {
    return () => {
      Object.values(familyListenersRef.current).forEach(listeners => {
        listeners?.forEach(unsub => unsub());
      });
    };
  }, []);

  // Render a family row - compact single-line layout using table-like fixed widths
  const renderFamilyRow = (familyId, index) => {
    const data = familyData[familyId] || {};
    const calculated = calculatedData[familyId] || {};
    const isCalculating = calculatingFamilies.has(familyId);

    const familyName = data.familyInfo?.name || familyId;
    const isSetup = data.isSetup;
    const hasEverSignedIn = data.hasEverSignedIn;
    const lastOnlinePing = data.lastOnlinePing;
    // Compute isOnline at render time so it stays fresh
    const isOnline = computeIsOnline(lastOnlinePing);
    const currentLocation = getLocationDisplay(data.currentScreen, isOnline, iglooNames);

    return (
      <div key={familyId} className="flex items-center py-1.5 px-3 border-b last:border-b-0 hover:bg-muted/30 text-xs">
        {/* Online indicator + Family name (clickable for timeline) */}
        <div className="flex items-center gap-2 w-[200px] flex-shrink-0">
          {isOnline ? (
            <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTimelineFamily({ familyId, familyName });
              setShowTimeline(true);
            }}
            className="font-medium truncate text-left hover:text-blue-600 hover:underline cursor-pointer"
            title={`View timeline for ${familyName} (${familyId})`}
          >
            {familyName}
          </button>
          {isOnline && data.wifiRssiDbm != null && (
            <WifiSignalBars rssiDbm={data.wifiRssiDbm} />
          )}
        </div>

        {/* Setup badge */}
        <div className="w-[70px] flex-shrink-0">
          <Badge variant={isSetup ? 'default' : 'secondary'} className="text-xs px-1.5 py-0">
            {isSetup ? 'Setup' : 'No'}
          </Badge>
        </div>

        {/* Ever Signed In */}
        <div className="w-[70px] flex-shrink-0">
          <Badge variant={hasEverSignedIn ? 'default' : 'outline'} className="text-xs px-1.5 py-0">
            {hasEverSignedIn ? 'Yes' : 'No'}
          </Badge>
        </div>

        {/* Online duration / Last online */}
        <div className="flex items-center gap-1 w-[100px] flex-shrink-0">
          <Clock className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          <span className={isOnline ? 'text-green-600' : 'text-muted-foreground'}>
            {isOnline
              ? (calculated.onlineDuration ? formatDuration(calculated.onlineDuration) : 'Now')
              : (lastOnlinePing ? formatRelativeTime(lastOnlinePing) : 'Never')
            }
          </span>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1 w-[110px] flex-shrink-0">
          {currentLocation.type === 'igloo' ? (
            <Snowflake className="w-3 h-3 text-blue-500 flex-shrink-0" />
          ) : currentLocation.type === 'home' ? (
            <Home className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          ) : (
            <Activity className="w-3 h-3 text-muted-foreground flex-shrink-0" />
          )}
          <span className={currentLocation.type === 'igloo' ? 'text-blue-600 font-medium truncate' : 'truncate'} title={currentLocation.name}>
            {currentLocation.name}
          </span>
        </div>

        {/* Igloo time (in/since) */}
        <div className="w-[75px] flex-shrink-0">
          <span className={calculated.iglooTimeInfo?.type === 'in_igloo' ? 'text-blue-600' : 'text-muted-foreground'}>
            {calculated.iglooTimeInfo ? formatDuration(calculated.iglooTimeInfo.duration) : '-'}
          </span>
        </div>

        {/* Igloo Hours 24h/7d */}
        <div className="flex items-center gap-1 w-[65px] flex-shrink-0">
          <Snowflake className="w-3 h-3 text-blue-400 flex-shrink-0" />
          <span className={(calculated.iglooSeconds24h || 0) > 0 ? 'text-blue-600' : ''}>
            {formatSecondsAsHours(calculated.iglooSeconds24h)}/{formatSecondsAsHours(calculated.iglooSeconds7d)}
          </span>
        </div>

        {/* Igloo Sessions 24h/7d */}
        <div className="flex items-center gap-1 w-[55px] flex-shrink-0">
          <Users className="w-3 h-3 text-blue-400 flex-shrink-0" />
          <span className={(calculated.iglooSessions24h || 0) > 0 ? 'text-blue-600' : ''}>
            {calculated.iglooSessions24h || 0}/{calculated.iglooSessions7d || 0}
          </span>
        </div>

        {/* Last Crash */}
        <div className="flex items-center gap-1 w-[100px] flex-shrink-0">
          {calculated.lastCrash ? (
            <>
              <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
              <span className="text-red-600" title={`${formatCrashReason(calculated.lastCrash.reason)} (${formatLastState(calculated.lastCrash.lastState)})`}>
                {formatRelativeTime(calculated.lastCrash.timestamp)}
              </span>
            </>
          ) : (
            <span className="text-green-600">None</span>
          )}
        </div>

        {/* Crashes 24h/7d */}
        <div className="flex items-center gap-1 w-[65px] flex-shrink-0">
          <Zap className="w-3 h-3 text-red-400 flex-shrink-0" />
          <span className={(calculated.crashesLast24h || 0) > 0 ? 'text-red-600 font-medium' : ''}>
            {calculated.crashesLast24h || 0}/{calculated.crashesLast7d || 0}
          </span>
        </div>

        {/* Last Power Cycle */}
        <div className="flex items-center gap-1 w-[100px] flex-shrink-0">
          {calculated.lastPowerCycle ? (
            <>
              <Zap className="w-3 h-3 text-orange-500 flex-shrink-0" />
              <span className="text-orange-600" title={`${formatCrashReason(calculated.lastPowerCycle.reason)} (${formatLastState(calculated.lastPowerCycle.lastState)})`}>
                {formatRelativeTime(calculated.lastPowerCycle.timestamp)}
              </span>
            </>
          ) : (
            <span className="text-green-600">None</span>
          )}
        </div>

        {/* Power Cycles 24h/7d */}
        <div className="flex items-center gap-1 w-[65px] flex-shrink-0">
          <Zap className="w-3 h-3 text-orange-400 flex-shrink-0" />
          <span className={(calculated.powerCyclesLast24h || 0) > 0 ? 'text-orange-600 font-medium' : ''}>
            {calculated.powerCyclesLast24h || 0}/{calculated.powerCyclesLast7d || 0}
          </span>
        </div>

        {/* Timeline button */}
        <div className="w-[36px] flex-shrink-0 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedTimelineFamily({ familyId, familyName });
              setShowTimeline(true);
            }}
            className="h-6 w-6 p-0"
            title="View Timeline"
          >
            <History className="w-3 h-3" />
          </Button>
        </div>

        {/* Refresh button */}
        <div className="w-[36px] flex-shrink-0 flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); calculateFamilyAnalytics(familyId); }}
            disabled={isCalculating}
            className="h-6 w-6 p-0"
          >
            <RefreshCw className={`w-3 h-3 ${isCalculating ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    );
  };

  // Calculate aggregate statistics for an account's devices
  // numOfFamilies is the expected total (set even before setup), families is the actual array of family IDs
  const calculateAccountAggregates = (families, numOfFamilies) => {
    let setupCount = 0;
    let signedInCount = 0;
    let onlineCount = 0;
    let inIglooCount = 0;
    let totalCrashes24h = 0;
    let totalCrashes7d = 0;
    let totalPowerCycles24h = 0;
    let totalPowerCycles7d = 0;
    let totalIglooSeconds24h = 0;
    let totalIglooSeconds7d = 0;
    let totalIglooSessions24h = 0;
    let totalIglooSessions7d = 0;
    let lastIglooTime = null;
    let lastCrashTime = null;
    let lastPowerCycleTime = null;

    for (const familyId of families) {
      const data = familyData[familyId] || {};
      const calculated = calculatedData[familyId] || {};

      if (data.isSetup) setupCount++;
      if (data.hasEverSignedIn) signedInCount++;

      const isOnline = computeIsOnline(data.lastOnlinePing);
      if (isOnline) onlineCount++;

      // Check if in igloo
      const currentScreen = data.currentScreen;
      const isInIgloo = isOnline && currentScreen &&
                       currentScreen !== 'home' &&
                       currentScreen !== 'training' &&
                       currentScreen !== 'sleep' &&
                       currentScreen !== 'loading';
      if (isInIgloo) inIglooCount++;

      // Aggregate crash/power cycle counts
      totalCrashes24h += calculated.crashesLast24h || 0;
      totalCrashes7d += calculated.crashesLast7d || 0;
      totalPowerCycles24h += calculated.powerCyclesLast24h || 0;
      totalPowerCycles7d += calculated.powerCyclesLast7d || 0;

      // Aggregate igloo time and session counts
      totalIglooSeconds24h += calculated.iglooSeconds24h || 0;
      totalIglooSeconds7d += calculated.iglooSeconds7d || 0;
      totalIglooSessions24h += calculated.iglooSessions24h || 0;
      totalIglooSessions7d += calculated.iglooSessions7d || 0;

      // Track most recent igloo time across all devices
      if (calculated.iglooTimeInfo) {
        const iglooTime = calculated.iglooTimeInfo.type === 'in_igloo'
          ? Date.now()  // Currently in igloo
          : Date.now() - calculated.iglooTimeInfo.duration;  // Time they left
        if (!lastIglooTime || iglooTime > lastIglooTime) {
          lastIglooTime = iglooTime;
        }
      }

      // Track most recent crash
      if (calculated.lastCrash?.timestamp) {
        if (!lastCrashTime || calculated.lastCrash.timestamp > lastCrashTime) {
          lastCrashTime = calculated.lastCrash.timestamp;
        }
      }

      // Track most recent power cycle
      if (calculated.lastPowerCycle?.timestamp) {
        if (!lastPowerCycleTime || calculated.lastPowerCycle.timestamp > lastPowerCycleTime) {
          lastPowerCycleTime = calculated.lastPowerCycle.timestamp;
        }
      }
    }

    // Use numOfFamilies for the total (expected count), fall back to families.length if not set
    const total = numOfFamilies || families.length;

    return {
      total,
      setupCount,
      signedInCount,
      onlineCount,
      inIglooCount,
      totalCrashes24h,
      totalCrashes7d,
      totalPowerCycles24h,
      totalPowerCycles7d,
      totalIglooSeconds24h,
      totalIglooSeconds7d,
      totalIglooSessions24h,
      totalIglooSessions7d,
      lastIglooTime,
      lastCrashTime,
      lastPowerCycleTime
    };
  };

  // Render account card
  const renderAccountCard = (account) => {
    const isExpanded = expandedAccounts[account.id];
    const families = account.families || [];
    const lastRefreshed = accountLastRefreshed[account.id];
    // Pass numOfFamilies so the account row shows the expected total even before setup
    const aggregates = calculateAccountAggregates(families, account.numOfFamilies);

    return (
      <div key={account.id} className="border rounded-lg bg-card overflow-hidden">
        {/* Account Header - Clickable to expand, with aggregated stats aligned to device columns */}
        <div
          className="flex items-center py-2 px-3 cursor-pointer hover:bg-muted/50 text-xs"
          onClick={() => toggleAccount(account.id, families)}
        >
          {/* Account name column - same width as Device column */}
          <div className="flex items-center gap-2 w-[200px] flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ChevronRight className="w-4 h-4 flex-shrink-0" />
            )}
            <Users className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className="font-medium truncate" title={account.email}>
              {account.email}
            </span>
          </div>

          {/* Setup count - aligned with Setup column */}
          <div className="w-[70px] flex-shrink-0">
            <span className={aggregates.setupCount === aggregates.total ? 'text-green-600' : 'text-muted-foreground'}>
              {aggregates.setupCount}/{aggregates.total}
            </span>
          </div>

          {/* Signed in count - aligned with Signed In column */}
          <div className="w-[70px] flex-shrink-0">
            <span className={aggregates.signedInCount === aggregates.total ? 'text-green-600' : 'text-muted-foreground'}>
              {aggregates.signedInCount}/{aggregates.total}
            </span>
          </div>

          {/* Online count - aligned with Online column */}
          <div className="flex items-center gap-1 w-[100px] flex-shrink-0">
            <span className={aggregates.onlineCount > 0 ? 'text-green-600' : 'text-muted-foreground'}>
              {aggregates.onlineCount}/{aggregates.total} online
            </span>
          </div>

          {/* In igloo count - aligned with Location column */}
          <div className="flex items-center gap-1 w-[110px] flex-shrink-0">
            {aggregates.inIglooCount > 0 ? (
              <>
                <Snowflake className="w-3 h-3 text-blue-500 flex-shrink-0" />
                <span className="text-blue-600">{aggregates.inIglooCount} in igloo</span>
              </>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>

          {/* Last igloo time - aligned with Igloo column */}
          <div className="w-[75px] flex-shrink-0">
            <span className="text-muted-foreground">
              {aggregates.lastIglooTime ? formatRelativeTime(aggregates.lastIglooTime) : '-'}
            </span>
          </div>

          {/* Total igloo hours 24h/7d - aligned with Hrs 24/7d column */}
          <div className="flex items-center gap-1 w-[65px] flex-shrink-0">
            <Snowflake className="w-3 h-3 text-blue-400 flex-shrink-0" />
            <span className={aggregates.totalIglooSeconds24h > 0 ? 'text-blue-600' : ''}>
              {formatSecondsAsHours(aggregates.totalIglooSeconds24h)}/{formatSecondsAsHours(aggregates.totalIglooSeconds7d)}
            </span>
          </div>

          {/* Total igloo sessions 24h/7d - aligned with Sess column */}
          <div className="flex items-center gap-1 w-[55px] flex-shrink-0">
            <Users className="w-3 h-3 text-blue-400 flex-shrink-0" />
            <span className={aggregates.totalIglooSessions24h > 0 ? 'text-blue-600' : ''}>
              {aggregates.totalIglooSessions24h}/{aggregates.totalIglooSessions7d}
            </span>
          </div>

          {/* Last crash - aligned with Last Crash column */}
          <div className="flex items-center gap-1 w-[100px] flex-shrink-0">
            {aggregates.lastCrashTime ? (
              <>
                <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0" />
                <span className="text-red-600">{formatRelativeTime(aggregates.lastCrashTime)}</span>
              </>
            ) : (
              <span className="text-green-600">None</span>
            )}
          </div>

          {/* Total crashes 24h/7d - aligned with 24h/7d column */}
          <div className="flex items-center gap-1 w-[65px] flex-shrink-0">
            <Zap className="w-3 h-3 text-red-400 flex-shrink-0" />
            <span className={aggregates.totalCrashes24h > 0 ? 'text-red-600 font-medium' : ''}>
              {aggregates.totalCrashes24h}/{aggregates.totalCrashes7d}
            </span>
          </div>

          {/* Last power cycle - aligned with Pwr Cycle column */}
          <div className="flex items-center gap-1 w-[100px] flex-shrink-0">
            {aggregates.lastPowerCycleTime ? (
              <>
                <Zap className="w-3 h-3 text-orange-500 flex-shrink-0" />
                <span className="text-orange-600">{formatRelativeTime(aggregates.lastPowerCycleTime)}</span>
              </>
            ) : (
              <span className="text-green-600">None</span>
            )}
          </div>

          {/* Total power cycles 24h/7d - aligned with 24h/7d column */}
          <div className="flex items-center gap-1 w-[65px] flex-shrink-0">
            <Zap className="w-3 h-3 text-orange-400 flex-shrink-0" />
            <span className={aggregates.totalPowerCycles24h > 0 ? 'text-orange-600 font-medium' : ''}>
              {aggregates.totalPowerCycles24h}/{aggregates.totalPowerCycles7d}
            </span>
          </div>

          {/* Status badges and shipping indicators - far right */}
          <div className="flex items-center gap-2 ml-auto">
            {account.preparingForShipping && (
              <div className="flex items-center gap-1 text-amber-600">
                <Package className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Preparing</span>
              </div>
            )}
            {account.shipped && (
              <div className="flex items-center gap-1 text-green-600">
                <Truck className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Shipped</span>
              </div>
            )}
          </div>
        </div>

        {/* Expanded Content - Family List */}
        {isExpanded && (
          <div className="border-t">
            {/* Header row - fixed widths to match data rows */}
            <div className="flex items-center py-1.5 px-3 bg-muted/50 text-xs text-muted-foreground border-b">
              <div className="w-[200px] flex-shrink-0 font-medium">Device</div>
              <div className="w-[70px] flex-shrink-0 font-medium">Setup</div>
              <div className="w-[70px] flex-shrink-0 font-medium">Signed In</div>
              <div className="w-[100px] flex-shrink-0 font-medium">Online</div>
              <div className="w-[110px] flex-shrink-0 font-medium">Location</div>
              <div className="w-[75px] flex-shrink-0 font-medium">Igloo</div>
              <div className="w-[65px] flex-shrink-0 font-medium">Hrs 24/7d</div>
              <div className="w-[55px] flex-shrink-0 font-medium">Sess</div>
              <div className="w-[100px] flex-shrink-0 font-medium">Last Crash</div>
              <div className="w-[65px] flex-shrink-0 font-medium">24h/7d</div>
              <div className="w-[100px] flex-shrink-0 font-medium">Pwr Cycle</div>
              <div className="w-[65px] flex-shrink-0 font-medium">24h/7d</div>
              <div className="w-[36px] flex-shrink-0"></div>
              <div className="w-[36px] flex-shrink-0"></div>
              <div className="ml-auto flex items-center gap-2">
                {lastRefreshed && (
                  <span className="text-xs">
                    {formatRelativeTime(lastRefreshed)}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    refreshAccountAnalytics(account.id, families);
                  }}
                  className="h-6 px-2 text-xs"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Refresh All
                </Button>
              </div>
            </div>

            {families.length === 0 ? (
              <div className="text-sm text-muted-foreground py-4 text-center">
                No families assigned to this account
              </div>
            ) : (
              <div className="bg-white">
                {families.map((familyId, index) => renderFamilyRow(familyId, index))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Show timeline view if selected
  if (showTimeline && selectedTimelineFamily) {
    return (
      <FamilyTimeline
        familyId={selectedTimelineFamily.familyId}
        familyName={selectedTimelineFamily.familyName}
        onBack={() => {
          setShowTimeline(false);
          setSelectedTimelineFamily(null);
        }}
      />
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            <span>Loading accounts...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const expandedCount = Object.keys(expandedAccounts).filter(k => expandedAccounts[k]).length;

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="w-5 h-5" />
            Fleet Health Overview
          </CardTitle>
          <div className="flex items-center gap-3">
            {globalLastRefreshed && (
              <span className="text-xs text-muted-foreground">
                Last refresh: {formatRelativeTime(globalLastRefreshed)}
              </span>
            )}
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Sort:</span>
              <Select value={healthSortMode} onValueChange={(value) => {
                setHealthSortMode(value);
                if (value === 'recentIgloo' && Object.keys(lastIglooTimes).length === 0) fetchIglooTimes();
              }}>
                <SelectTrigger className="h-7 w-[150px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alphabetic">Alphabetic</SelectItem>
                  <SelectItem value="recentSetup">Recent Setup</SelectItem>
                  <SelectItem value="recentSignin">Recent Signin</SelectItem>
                  <SelectItem value="recentIgloo">Recent Igloo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAllAccounts}
                disabled={expandedCount === accounts.length}
                className="h-7 px-2 text-xs"
              >
                <ChevronDown className="w-3.5 h-3.5 mr-1" />
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAllAccounts}
                disabled={expandedCount === 0}
                className="h-7 px-2 text-xs"
              >
                <ChevronRight className="w-3.5 h-3.5 mr-1" />
                Collapse All
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshAllAnalytics}
              disabled={isGlobalRefreshing || expandedCount === 0}
              className="h-7 px-3 text-xs"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isGlobalRefreshing ? 'animate-spin' : ''}`} />
              Refresh Expanded
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-2">
          {accounts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No accounts found.</p>
            </div>
          ) : (
            <>
              {iglooTimesLoading && (
                <div className="text-center py-2 text-xs text-muted-foreground">Loading igloo data...</div>
              )}
              {sortedAccounts.map(account => renderAccountCard(account))}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default HealthTab;
